import React from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import { Wallet, ArrowLeft, Clock, Calendar, ArrowRight, Menu, X } from 'lucide-react';
import { getBlogPost, blogPosts } from '../data/blogPosts';
import { useState } from 'react';

const categoryColors = {
  'Organização': 'bg-blue-100 text-blue-700',
  'Planejamento': 'bg-purple-100 text-purple-700',
  'Poupança': 'bg-emerald-100 text-emerald-700',
  'Segurança Financeira': 'bg-amber-100 text-amber-700',
  'Orçamento': 'bg-rose-100 text-rose-700',
};

function formatDate(iso) {
  const [year, month, day] = iso.split('-');
  const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
  return `${parseInt(day)} de ${months[parseInt(month) - 1]} de ${year}`;
}

// Minimal markdown renderer — handles ## headings, **bold**, bullet lists, tables
function renderMarkdown(text) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // H2
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="text-2xl font-bold text-[#1B2A4A] mt-10 mb-4">
          {line.slice(3)}
        </h2>
      );
      i++;
      continue;
    }

    // Table — detect header separator
    if (
      i + 1 < lines.length &&
      lines[i + 1].match(/^\|[-| :]+\|$/)
    ) {
      const headers = line.split('|').filter(Boolean).map((s) => s.trim());
      i += 2; // skip header + separator
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        rows.push(lines[i].split('|').filter(Boolean).map((s) => s.trim()));
        i++;
      }
      elements.push(
        <div key={`table-${i}`} className="overflow-x-auto my-6">
          <table className="w-full border-2 border-slate-200 rounded-xl text-base">
            <thead className="bg-[#1B2A4A] text-white">
              <tr>
                {headers.map((h, hi) => (
                  <th key={hi} className="px-4 py-3 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 border-t border-slate-200">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bullet list
    if (line.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="list-disc list-outside pl-6 my-4 space-y-2">
          {items.map((item, ii) => (
            <li key={ii} className="text-lg text-slate-700" dangerouslySetInnerHTML={{ __html: boldify(item) }} />
          ))}
        </ul>
      );
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} className="border-l-4 border-[#047857] pl-5 my-6 bg-emerald-50 py-3 rounded-r-xl">
          <p className="text-lg font-semibold text-[#1B2A4A]" dangerouslySetInnerHTML={{ __html: boldify(line.slice(2)) }} />
        </blockquote>
      );
      i++;
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph
    elements.push(
      <p key={i} className="text-lg text-slate-700 leading-relaxed my-4" dangerouslySetInnerHTML={{ __html: boldify(line) }} />
    );
    i++;
  }

  return elements;
}

function boldify(text) {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-[#1B2A4A]">$1</strong>');
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const post = getBlogPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b-2 border-slate-200 z-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#1B2A4A] rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-[#1B2A4A]">Bolso</span>
                <span className="text-sm font-medium text-slate-500 ml-1">Sob Controle</span>
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-base font-medium text-slate-600 hover:text-[#1B2A4A]">Início</Link>
              <Link to="/blog" className="text-base font-semibold text-[#1B2A4A] border-b-2 border-[#1B2A4A]">Blog</Link>
              <button onClick={() => navigate('/auth')} className="text-base font-semibold text-[#1B2A4A] hover:underline">
                Entrar
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="h-12 px-6 bg-[#047857] hover:bg-[#059669] text-white rounded-xl text-base font-bold transition-colors"
              >
                Criar Conta Grátis
              </button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-[#1B2A4A]"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200 space-y-3">
              <Link to="/" className="block py-2 text-lg font-medium text-slate-700" onClick={() => setMobileMenuOpen(false)}>Início</Link>
              <Link to="/blog" className="block py-2 text-lg font-semibold text-[#1B2A4A]" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
              <button onClick={() => navigate('/auth')} className="block w-full py-2 text-lg font-medium text-slate-700 text-left">Entrar</button>
              <button
                onClick={() => navigate('/auth')}
                className="block w-full py-3 bg-[#047857] text-white rounded-xl text-lg font-bold text-center"
              >
                Criar Conta Grátis
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Article */}
      <main className="pt-28 md:pt-36 pb-20 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-base text-slate-500 hover:text-[#1B2A4A] mb-8">
            <ArrowLeft size={16} />
            Voltar ao Blog
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${categoryColors[post.categoria] || 'bg-slate-100 text-slate-700'}`}>
                {post.categoria}
              </span>
              <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                <Clock size={14} />
                {post.tempoLeitura} de leitura
              </span>
              <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                <Calendar size={14} />
                {formatDate(post.dataPublicacao)}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1B2A4A] leading-tight mb-4">
              {post.titulo}
            </h1>
            <p className="text-xl text-slate-600">{post.resumo}</p>
          </div>

          <hr className="border-2 border-slate-200 mb-8" />

          {/* Content */}
          <article className="prose-bolso">
            {renderMarkdown(post.conteudo)}
          </article>

          <hr className="border-2 border-slate-200 mt-12 mb-10" />

          {/* CTA inline */}
          <div className="bg-[#1B2A4A] rounded-2xl p-8 text-center mb-14">
            <h3 className="text-2xl font-bold text-white mb-3">
              Coloque em prática com o Bolso Sob Controle
            </h3>
            <p className="text-slate-300 text-lg mb-6">
              Registre receitas, despesas e metas em um lugar só. Grátis para começar.
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="h-12 px-8 bg-[#047857] hover:bg-[#059669] text-white rounded-xl text-base font-bold transition-colors"
            >
              Criar Conta Grátis
            </button>
          </div>

          {/* Related articles */}
          {related.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-[#1B2A4A] mb-4">Leia também</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="group bg-white border-2 border-slate-200 rounded-xl p-5 hover:border-[#1B2A4A] hover:shadow-md transition-all"
                  >
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold mb-2 ${categoryColors[p.categoria] || 'bg-slate-100 text-slate-700'}`}>
                      {p.categoria}
                    </span>
                    <p className="text-base font-bold text-[#1B2A4A] group-hover:underline mb-1">{p.titulo}</p>
                    <span className="inline-flex items-center gap-1 text-sm text-[#047857] font-semibold">
                      Ler <ArrowRight size={12} />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t-2 border-slate-200 py-8 px-4 md:px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1B2A4A] rounded-lg flex items-center justify-center">
              <Wallet className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-[#1B2A4A]">Bolso Sob Controle</span>
          </Link>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Bolso Sob Controle. Todos os direitos reservados.
          </p>
          <div className="flex gap-4">
            <Link to="/" className="text-sm text-slate-500 hover:text-[#1B2A4A]">Início</Link>
            <Link to="/blog" className="text-sm text-slate-500 hover:text-[#1B2A4A]">Blog</Link>
            <Link to="/auth" className="text-sm text-slate-500 hover:text-[#1B2A4A]">Entrar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
